import React, { useRef, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Toaster } from '../components/common';
import { uploadResume } from '../services/onboardingNewService';
import {
  adjustEditTextareaHeight,
  extractLinkedInUrl,
  isOnlyUrl,
  isValidContent,
} from '../utils';
import useUploader from './useUploader';

interface UseOnboardingInputOptions {
  answer: string;
  setAnswer: (answer: string) => void;
  isAnswerUpdating: boolean;
  currentQuestionCode?: string;
}

interface ResumeLinkedInResponse {
  bio?: string;
  linkedin?: string;
  resume?: { name: string; [key: string]: any };
}

const MIN_ROWS = 1;
const MAX_ROWS = 6;

/**
 * Custom hook that extracts shared logic between Footer and EditFooter components.
 * Handles textarea management, file uploads, and resume/LinkedIn bio processing.
 */
export const useOnboardingInput = ({
  answer,
  setAnswer,
  isAnswerUpdating: _isAnswerUpdating,
  currentQuestionCode,
}: UseOnboardingInputOptions) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { files, errors, setFiles, handleFileChange, clearFiles } = useUploader(
    fileInputRef,
    {
      fileTypes: ['pdf', 'doc', 'docx'],
      multiple: false,
      maxLength: 1,
    }
  );

  const isResumeLinkedInQuestion = currentQuestionCode === 'resume_linkedin_bio';

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      adjustEditTextareaHeight(textareaRef.current, MIN_ROWS, MAX_ROWS);
    }
  }, [answer]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setAnswer(e.target.value);
    },
    [setAnswer]
  );

  /**
   * Shows an error toast with consistent styling
   */
  const showErrorToast = useCallback((message: string) => {
    toast.custom(t => <Toaster message={message} type='error' id={t.id} />);
  }, []);

  /**
   * Validates the answer content
   * @returns true if valid, false otherwise (shows toast on invalid)
   */
  const validateAnswer = useCallback(
    (filesCount: number): boolean => {
      if (answer.length === 0 && filesCount === 0) {
        showErrorToast('Please enter your answer');
        return false;
      }

      if (!isValidContent(answer)) {
        showErrorToast('Please enter a meaningful answer');
        return false;
      }

      return true;
    },
    [answer, showErrorToast]
  );

  /**
   * Processes resume/LinkedIn bio question and returns formatted response
   * @returns null if validation fails, response object otherwise
   */
  const processResumeLinkedInAnswer = useCallback(
    async (): Promise<ResumeLinkedInResponse | null> => {
      const userResponse: ResumeLinkedInResponse = {};

      if (!isOnlyUrl(answer)) {
        userResponse.bio = answer;
      }

      const linkedInUrl = extractLinkedInUrl(answer);
      if (linkedInUrl) {
        userResponse.linkedin = linkedInUrl;
      }

      if (files.length > 0) {
        try {
          const response = await uploadResume(files[0]);
          if (response.code === 200) {
            userResponse.resume = { ...response.result, name: files[0].name };
          } else {
            showErrorToast(response.message);
            return null;
          }
        } catch (error) {
          console.error('Resume upload failed:', error);
          showErrorToast('Failed to upload resume');
          return null;
        }
      }

      if (Object.keys(userResponse).length === 0) {
        showErrorToast('Please enter your answer');
        return null;
      }

      return userResponse;
    },
    [answer, files, showErrorToast]
  );

  /**
   * Parses existing resume/LinkedIn answer for edit mode
   */
  const parseExistingAnswer = useCallback(
    (existingAnswer: string) => {
      if (!isResumeLinkedInQuestion) return;

      try {
        const answerObject = JSON.parse(existingAnswer);

        if (isOnlyUrl(answerObject?.bio) && answerObject?.linkedin) {
          setAnswer(answerObject?.linkedin);
        } else {
          setAnswer(answerObject?.bio || '');
        }

        if (answerObject?.resume) {
          setFiles([answerObject?.resume as unknown as File]);
        }
      } catch (error) {
        console.error('Failed to parse existing answer:', error);
      }
    },
    [isResumeLinkedInQuestion, setAnswer, setFiles]
  );

  const openFileDialog = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return {
    // Refs
    textareaRef,
    fileInputRef,
    // File state
    files,
    errors,
    clearFiles,
    handleFileChange,
    openFileDialog,
    // Input handlers
    handleChange,
    // Validation & processing
    validateAnswer,
    processResumeLinkedInAnswer,
    parseExistingAnswer,
    showErrorToast,
    // Constants
    isResumeLinkedInQuestion,
    MIN_ROWS,
    MAX_ROWS,
  };
};

export default useOnboardingInput;
